import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { HeartPulse, ArrowRight, Lock, Sparkles, Building2, TrendingUp, Shield, Sliders } from 'lucide-react';
import { Button } from '../ui/button';
import ActionCenter from './ActionCenter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v, { compact = true, sign = false } = {}) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const n = Number(v);
  const p = sign && n > 0 ? '+' : '';
  if (compact) {
    if (Math.abs(n) >= 1e6) return `${p}$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `${p}$${(n / 1e3).toFixed(0)}K`;
  }
  return `${p}$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const STATUS_STYLES = {
  healthy: { ring: '#22c55e', text: 'text-emerald-500', dot: 'bg-emerald-500' },
  needs_attention: { ring: '#eab308', text: 'text-yellow-500', dot: 'bg-yellow-500' },
  at_risk: { ring: '#f97316', text: 'text-orange-500', dot: 'bg-orange-500' },
  critical: { ring: '#ef4444', text: 'text-red-500', dot: 'bg-red-500' },
};

/**
 * Dashboard portfolio hero — appears ABOVE the existing stats grid.
 *
 * Premium: greeting, radial Portfolio Health Score, snapshot KPIs, compact
 * Action Center, CTA to Portfolio Doctor and My Portfolio.
 *
 * Free / Pro: an upgrade preview with the same visual language.
 * Empty portfolio: friendly empty-state pointing at /analyze.
 */
export default function DashboardHero({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  const isPremium = user?.subscription_plan === 'premium';

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    if (!isPremium) {
      setGated(true);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/portfolio-doctor`, { withCredentials: true });
        setDoctor(resp.data);
      } catch (e) {
        if (e.response?.status === 403) setGated(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, isPremium]);

  if (loading) {
    return (
      <div className="animate-pulse mb-10 h-56 rounded-3xl bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800" />
    );
  }

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t('dashboardHero.greeting_morning', { name: user?.name?.split(' ')[0] || '' });
    if (h < 18) return t('dashboardHero.greeting_afternoon', { name: user?.name?.split(' ')[0] || '' });
    return t('dashboardHero.greeting_evening', { name: user?.name?.split(' ')[0] || '' });
  };

  // --- Premium paywall preview for Free / Pro ---
  if (gated) {
    return (
      <div
        data-testid="dashboard-hero-paywall"
        className="mb-10 relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#12355B] via-[#1565C0] to-[#12355B] text-white p-8 shadow-2xl"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 90% 20%, rgba(255,215,0,0.55) 0%, transparent 55%)' }} />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-white/60 font-bold mb-2">{greeting()}</p>
            <h2 className="text-2xl sm:text-3xl font-primary font-extrabold tracking-tight mb-2">
              {t('dashboardHero.paywallTitle')}
            </h2>
            <p className="text-white/70 max-w-lg">{t('dashboardHero.paywallSubtitle')}</p>
          </div>
          <Button
            data-testid="hero-upgrade-btn"
            onClick={() => navigate('/pricing')}
            className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#12355B] rounded-xl px-6 py-3 font-bold hover:-translate-y-[1px] transition-all"
          >
            <Lock className="w-4 h-4 mr-2" />
            {t('dashboardHero.paywallCta')}
          </Button>
        </div>
      </div>
    );
  }

  // --- Empty portfolio state ---
  if (!doctor || doctor.empty) {
    return (
      <div className="mb-10 bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-3xl p-8 text-center">
        <p className="text-xs uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">{greeting()}</p>
        <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <HeartPulse className="w-7 h-7 text-slate-400" />
        </div>
        <p className="text-slate-600 dark:text-slate-300 mb-4">{t('dashboardHero.empty')}</p>
        <Button
          data-testid="hero-empty-cta"
          onClick={() => navigate('/analyze')}
          className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl px-6 font-semibold"
        >
          {t('dashboardHero.emptyCta')}
        </Button>
      </div>
    );
  }

  // --- Full premium hero ---
  const status = doctor.health_status?.key || 'critical';
  const style = STATUS_STYLES[status] || STATUS_STYLES.critical;
  const score = doctor.portfolio_health_score;
  const m = doctor.metrics;

  return (
    <div className="mb-10 space-y-4">
      {/* Hero card */}
      <div
        data-testid="dashboard-hero"
        className="relative overflow-hidden rounded-3xl bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 shadow-card p-6 sm:p-8"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ backgroundImage: `radial-gradient(circle at 90% 20%, ${style.ring}20 0%, transparent 55%)` }} />
        <div className="relative grid grid-cols-1 lg:grid-cols-[auto,1fr,auto] gap-6 items-center">
          {/* Score ring */}
          <div className="flex justify-center lg:justify-start">
            <ScoreRing score={score} status={status} />
          </div>
          {/* Greeting + status */}
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">{greeting()}</p>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-slate-600 dark:text-slate-300 text-lg">{t('dashboardHero.portfolioIs')}</span>
              <span className={`w-2 h-2 rounded-full ${style.dot}`} />
              <span data-testid="hero-status" className={`font-primary font-extrabold text-xl ${style.text}`}>
                {t(`portfolioDoctor.status.${status}`)}
              </span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              <span className={m.monthly_cash_flow >= 0 ? 'text-emerald-500 font-semibold' : 'text-red-500 font-semibold'}>
                {fmtMoney(m.monthly_cash_flow, { sign: true })}
              </span>{' '}
              {t('dashboardHero.monthlyCashFlow')}
            </p>
          </div>
          {/* CTAs — My Portfolio sits on top of Portfolio Doctor so the
              broader overview is the first entry point; the deeper
              diagnostic view is one tap below it. */}
          <div className="flex flex-col gap-2 min-w-[180px]">
            <Button
              data-testid="hero-portfolio-cta"
              onClick={() => navigate('/portfolio')}
              className="bg-gradient-to-r from-[#12355B] to-[#1565C0] hover:from-[#1565C0] hover:to-[#12355B] text-white rounded-xl px-5 font-semibold"
            >
              <Building2 className="w-4 h-4 mr-2" />
              {t('dashboardHero.viewMyPortfolio')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              data-testid="hero-doctor-cta"
              onClick={() => navigate('/portfolio-doctor')}
              className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-xl px-5 font-semibold"
            >
              <HeartPulse className="w-4 h-4 mr-2" />
              {t('dashboardHero.viewPortfolioDoctor')}
            </Button>
          </div>
        </div>

        {/* Snapshot KPIs */}
        <div className="relative mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 md:grid-cols-4 gap-4">
          <SnapshotCell testId="hero-snap-value" icon={Building2} label={t('dashboardHero.snapshotValue')} value={fmtMoney(m.total_value)} />
          <SnapshotCell testId="hero-snap-equity" icon={TrendingUp} label={t('dashboardHero.snapshotEquity')} value={fmtMoney(m.total_equity)} />
          <SnapshotCell testId="hero-snap-debt" icon={Shield} label={t('dashboardHero.snapshotDebt')} value={fmtMoney(m.total_debt)} />
          <SnapshotCell testId="hero-snap-ltv" icon={Sliders} label={t('dashboardHero.snapshotLtv')} value={m.portfolio_ltv === null ? '—' : `${Math.round(m.portfolio_ltv)}%`} />
        </div>
      </div>

      {/* Compact Action Center */}
      <ActionCenter variant="compact" />
    </div>
  );
}

function ScoreRing({ score, status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.critical;
  const size = 140;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = ((score || 0) / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor"
          className="text-slate-200 dark:text-slate-700" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={style.ring}
          strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${p} ${c}`}
          style={{ filter: `drop-shadow(0 0 10px ${style.ring}55)`, transition: 'stroke-dasharray 1.2s ease' }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-4xl font-primary font-extrabold tracking-tight" style={{ color: style.ring }}>{score ?? '—'}</span>
        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">/ 100</span>
      </div>
    </div>
  );
}

function SnapshotCell({ testId, icon: Icon, label, value }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3 text-slate-400" />
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      </div>
      <p className="text-lg font-primary font-extrabold text-[#12355B] dark:text-white">{value}</p>
    </div>
  );
}
